import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { pageContext } from 'ctm-util-functions';
import { Text, Flexbox, Anchor, Icon } from 'ctm-components';
import { formHelpersSelector, verticalLinkContentSelector } from 'ctm-selectors';
import { useBrandContentFactory } from 'ctm-app/client/hooks/useBrandContentFactory';

const DisclaimerContent = () => {
    const disclaimerContentElement = useRef(null);
    /**
     * When a content from the branding content has an element with attribute [data-id=expandable],
     * it will add the expandable styles to that element and provide the show more/less button
     * Updating this will ensure the button shows appropriately as the reference will only be set
     * after the rerender
     */
    const [expandableContent, setExpandableContent] = useState(false);
    const dispatch = useDispatch();
    const isDisclaimerContentExpanded = useSelector((state) => formHelpersSelector(state).get('isDisclaimerContentExpanded'));
    const howComparisonWorksUrl = useSelector((state) => verticalLinkContentSelector(state).get('footer_disclaimer_how_comparison_works_url'));
    const fsgDocumentUrl = useSelector((state) => verticalLinkContentSelector(state).get('footer_disclaimer_financial_services_guide_url'));
    const termsOfUseUrl = useSelector((state) => verticalLinkContentSelector(state).get('footer_disclaimer_terms_of_use_url'));
    const brandContentFactory = useBrandContentFactory();
    const pageState = pageContext.getProps().pageState || {};
    const isCarResultPage = ['results'].includes(pageState.pageName) && pageContext.getVertical() === 'car-insurance';

    const disclaimerContent = brandContentFactory.footerDisclaimer({
        comparisonUrl: howComparisonWorksUrl,
        fsgUrl: fsgDocumentUrl,
        termsOfUseUrl
    });

    /**
     * using the disclaimer content as this unique (to a brand) string will be easier for equality checks
     */
    useEffect(() => {
        setExpandableContent(disclaimerContentElement.current?.querySelector('[data-id=expandable]') || false);
    }, [disclaimerContent]);

    const isPageNameVerifyOrConfirmationOrQuotesExpired = ['verify', 'confirmation', 'quotes_expired'].includes(pageState.pageName);
    const customVariant = isPageNameVerifyOrConfirmationOrQuotesExpired ? 'underline_anchors_verify' : 'underline_anchors';
    const shouldExpandContent = isCarResultPage || isDisclaimerContentExpanded;

    if (expandableContent) {
        if (shouldExpandContent) {
            expandableContent.style.removeProperty('opacity');
            expandableContent.style.removeProperty('height');
            expandableContent.style.removeProperty('overflow');
            expandableContent.style.removeProperty('margin');
        } else {
            expandableContent.style.opacity = 0;
            expandableContent.style.height = 0;
            expandableContent.style.overflow = 'hidden';
            expandableContent.style.margin = 0;
        }
    }

    /**
     * Toggle the disclaimer content actions
     * @param {Boolean} isExpanded - the boolean value to check if the content is expanded/collapsed
     */
    const toggleDisclaimerContent = (isExpanded) => {
        dispatch({
            type: 'SET_VALUE',
            identifier: 'helpers.isDisclaimerContentExpanded',
            value: isExpanded,
            stateKey: 'vertical.quote'
        });
    };

    /**
     * Get the show more/less toggle links
     * @returns {Node} - the Flexbox containing the show more/less anchors and icons
     */
    const getToggleLinks = () => {
        const iconVariant = isDisclaimerContentExpanded ? 'ctm_angle_down' : 'ctm_angle_up';
        const toggleText = isDisclaimerContentExpanded ? 'Show less' : 'Show more';

        return (
            <Flexbox display="flex">
                <Anchor size="small" href="javascript:;" onClick={() => toggleDisclaimerContent(!isDisclaimerContentExpanded)}>
                    <Icon color="inherit" margin="right" variant={iconVariant} />
                    {toggleText}
                </Anchor>
            </Flexbox>
        );
    };

    return (
        <Flexbox display="flex" flex={1} column variant={isPageNameVerifyOrConfirmationOrQuotesExpired ? 'verify_content' : ''}>
            <Text
                ref={disclaimerContentElement}
                variant={customVariant}
                size="small"
                innerHTML={disclaimerContent} />
            {expandableContent && !isCarResultPage && getToggleLinks()}
        </Flexbox>
    );
};

export { DisclaimerContent };
